import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PrivacyTab = () => {
  const [dataProcessing, setDataProcessing] = useState({
    analytics: true,
    marketing: false,
    thirdParty: false,
    improvement: true
  });
  const [exportRequested, setExportRequested] = useState(false);
  const [deleteRequested, setDeleteRequested] = useState(false);

  const handleDataProcessingChange = (key, checked) => {
    setDataProcessing(prev => ({
      ...prev,
      [key]: checked
    }));
  };

  const handleExportData = () => {
    console.log('Initiating data export...');
    setExportRequested(true);
    // In a real app, this would trigger the export process
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      console.log('Initiating account deletion...');
      setDeleteRequested(true);
      // In a real app, this would start the deletion process
    }
  };

  const handleSavePrivacySettings = () => {
    console.log('Saving privacy settings...', dataProcessing);
  };

  return (
    <div className="space-y-8">
      {/* Data Processing Consent */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Data Processing Consent</h3>
        </div>
        
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Control how your data is processed and used. You can withdraw consent at any time.
          </p>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={dataProcessing?.analytics}
                onChange={(e) => handleDataProcessingChange('analytics', e?.target?.checked)}
                className="mt-1"
              />
              <div>
                <h4 className="font-medium text-foreground">Analytics & Performance</h4>
                <p className="text-sm text-muted-foreground">
                  Allow collection of anonymized usage data to improve application performance and user experience.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={dataProcessing?.marketing}
                onChange={(e) => handleDataProcessingChange('marketing', e?.target?.checked)}
                className="mt-1"
              />
              <div>
                <h4 className="font-medium text-foreground">Marketing Communications</h4>
                <p className="text-sm text-muted-foreground">
                  Receive personalized product recommendations and promotional content based on your usage patterns.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={dataProcessing?.thirdParty}
                onChange={(e) => handleDataProcessingChange('thirdParty', e?.target?.checked)}
                className="mt-1"
              />
              <div>
                <h4 className="font-medium text-foreground">Third-Party Integrations</h4>
                <p className="text-sm text-muted-foreground">
                  Allow data sharing with trusted third-party services for enhanced functionality (e.g., breach monitoring).
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={dataProcessing?.improvement}
                onChange={(e) => handleDataProcessingChange('improvement', e?.target?.checked)}
                className="mt-1"
              />
              <div>
                <h4 className="font-medium text-foreground">Product Improvement</h4>
                <p className="text-sm text-muted-foreground">
                  Use aggregated data to develop new features and improve existing functionality.
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="default"
            onClick={handleSavePrivacySettings}
            iconName="Save"
            iconPosition="left"
          >
            Save Privacy Settings
          </Button>
        </div>
      </div>
      {/* Data Rights */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Download" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Your Data Rights</h3>
        </div>
        
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Under GDPR and other privacy regulations, you have rights regarding your personal data.
          </p>

          {/* Data Export */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-foreground mb-2">Export Your Data</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Download a copy of all your personal data in a machine-readable format. This includes your vault items,
                  sharing history, and account information.
                </p>
                {exportRequested && (
                  <div className="flex items-center space-x-2 text-sm text-success mb-4">
                    <Icon name="CheckCircle" size={16} />
                    <span>Export request submitted. You'll receive an email when ready.</span>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                onClick={handleExportData}
                disabled={exportRequested}
                iconName="Download"
                iconPosition="left"
              >
                {exportRequested ? 'Requested' : 'Export Data'}
              </Button>
            </div>
          </div>

          {/* Data Portability */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-foreground mb-2">Data Portability</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Request your data in a format that can be easily transferred to another password manager.
                  Includes CSV export and migration guides.
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowRight"
                iconPosition="left"
              >
                Request Transfer
              </Button>
            </div>
          </div>

          {/* Data Correction */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-foreground mb-2">Correct Your Data</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  If you believe any of your personal information is inaccurate or incomplete, you can request corrections.
                </p>
              </div>
              <Button
                variant="outline"
                iconName="Edit"
                iconPosition="left"
              >
                Request Correction
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Account Deletion */}
      <div className="bg-card border border-error/20 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Trash2" size={20} className="text-error" />
          <h3 className="text-lg font-semibold text-foreground">Delete Account</h3>
        </div>
        
        <div className="space-y-4">
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-2">Permanent Account Deletion</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Deleting your account will permanently remove all your data, including:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                  <li>• All vault items and passwords</li>
                  <li>• Sharing history and permissions</li>
                  <li>• Account settings and preferences</li>
                  <li>• Audit logs and activity history</li>
                  <li>• Device registrations and trusted devices</li>
                </ul>
                <p className="text-sm font-medium text-error">
                  This action cannot be undone. Please export your data first if needed.
                </p>
              </div>
            </div>
          </div>

          {deleteRequested ? (
            <div className="flex items-center space-x-2 text-sm text-warning">
              <Icon name="Clock" size={16} />
              <span>Account deletion requested. You have 30 days to cancel this request.</span>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Need help? Contact our support team before deleting your account.
              </p>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Account
              </Button>
            </div>
          )}
        </div>
      </div>
      {/* Privacy Policy & Terms */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="FileText" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Legal Documents</h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Privacy Policy</h4>
              <p className="text-sm text-muted-foreground">Last updated: September 1, 2025</p>
            </div>
            <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right">
              View
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Terms of Service</h4>
              <p className="text-sm text-muted-foreground">Last updated: September 1, 2025</p>
            </div>
            <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right">
              View
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-foreground">Cookie Policy</h4>
              <p className="text-sm text-muted-foreground">Last updated: August 15, 2025</p>
            </div>
            <Button variant="ghost" size="sm" iconName="ExternalLink" iconPosition="right">
              View
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyTab;